<script lang="ts" setup>
import { AbcDropdown, AbcHeading } from "@ordbogen-npm/abc-dsk";
import { useEventListener } from "@vueuse/core";
import { isString, uniq, upperFirst } from "lodash-es";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import InflectionTrainerService from "@/common/api/inflection-trainer.service";
import { useRouterQuerySync } from "@/common/composables/use-router-query-sync";
import { flushVueQueue } from "@/common/utils";
import AdjectiveInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/german/AdjectiveInflectionSchemeModal.vue";
import NounInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/german/NounInflectionSchemeModal.vue";
import VerbInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/german/VerbInflectionSchemeModal.vue";
import { mapGrammaticalLabelsToLevel } from "@/components/article/dictsys/level-mapping";
import EmptyResultContainer from "@/components/inflectionTrainer/EmptyResultContainer.vue";
import FuzzySuggestionsMessage from "@/components/inflectionTrainer/FuzzySuggestionsMessage.vue";
import PlaceholderAdjectiveInflectionScheme from "@/components/inflectionTrainer/german/PlaceholderAdjectiveInflectionScheme.vue";
import PlaceholderNounInflectionScheme from "@/components/inflectionTrainer/german/PlaceholderNounInflectionScheme.vue";
import PlaceholderVerbInflectionScheme from "@/components/inflectionTrainer/german/PlaceholderVerbInflectionScheme.vue";
import InflectionSchemeArticle from "@/components/inflectionTrainer/InflectionSchemeArticle.vue";
import InflectionSchemeCellDiff from "@/components/inflectionTrainer/InflectionSchemeCellDiff.vue";
import InflectionSchemeCellHighlight from "@/components/inflectionTrainer/InflectionSchemeCellHighlight.vue";
import WordClassSwitchedInfoBox from "@/components/inflectionTrainer/WordClassSwitchedInfoBox.vue";
import WordExistsInOtherWordClassesInfoBox from "@/components/inflectionTrainer/WordExistsInOtherWordClassesInfoBox.vue";
import WordExistsInOtherWordClassInfoBox from "@/components/inflectionTrainer/WordExistsInOtherWordClassInfoBox.vue";
import SearchBarBase from "@/components/searchBar/SearchBarBase.vue";
import { MatomoHelper } from "@/helpers";
import { useSettingsStore } from "@/stores";
import {
  type DictsysGermanAdjectiveInflectionScheme,
  type DictsysGermanInflectionScheme,
  type DictsysGermanNounInflectionScheme,
  type DictsysGermanVerbInflectionScheme,
  MatomoEventAction,
  MatomoEventCategory
} from "@/types";
import type { Level } from "@/types/dictionary";
import {
  type ExactResult,
  type FuzzySuggestions,
  isWordClass,
  type SearchResponse,
  type WordClass,
  type WordClassLabelSet
} from "@/types/inflection-trainer.type";

const { t } = useI18n();
const route = useRoute();
const settingsStore = useSettingsStore();

const routeParameterSyncMethod = ref<"push" | "replace">("push");

const { value: query, sync: syncQueryParam } = useRouterQuerySync<string>("query", {
  method: routeParameterSyncMethod,
  autoSync: false,
  defaultValue: ""
});

const state = ref<"initial" | "loading" | "success" | "error">("initial");
const showPlaceholder = ref<boolean>(true);

const genderMap = {
  maskulinum: "masculine",
  femininum: "feminine",
  neutrum: "neutral"
} as const;

const { value: searchWordClass } = useRouterQuerySync<WordClass>("wordClass", {
  method: routeParameterSyncMethod,
  defaultValue: "noun",
  validator: (value): value is WordClass => isString(value) && isWordClass(value)
});

const wordClassLevel = computed<Level>(() => settingsStore.activeWordClassLevel);

const wordClassLabels: WordClassLabelSet = {
  noun: {
    danish: "navneord",
    latin: "substantiv"
  },
  verb: {
    danish: "udsagnsord",
    latin: "verbum"
  },
  adjective: {
    danish: "tillægsord",
    latin: "adjektiv"
  }
};

const dropdownWordClasses = computed(() =>
  Object.keys(wordClassLabels).map((wordClass) => ({
    label: mapGrammaticalLabelsToLevel(
      wordClassLevel.value,
      upperFirst(wordClassLabels[wordClass].danish),
      upperFirst(wordClassLabels[wordClass].latin)
    ),
    value: wordClass
  }))
);

const response = ref<SearchResponse>();

const exactResults = computed<ExactResult[]>(() => {
  if (response.value?.type !== "exact") {
    return [];
  }

  return response.value.results.filter((result) => result.wordClass === searchWordClass.value);
});

const fuzzySuggestions = computed<FuzzySuggestions[]>(() => {
  if (response.value?.type !== "fuzzy") {
    return [];
  }

  const suggestions: FuzzySuggestions[] = [];

  response.value.results.forEach((result) => {
    const groupIndex = suggestions.findIndex((group) => group.wordClass === result.wordClass);

    if (groupIndex === -1) {
      suggestions.push({
        suggestions: [result.word],
        wordClass: result.wordClass
      });

      return;
    }

    // We don't want more than 3 suggestions for each word class
    if (suggestions[groupIndex].suggestions.length === 3) {
      return;
    }

    suggestions[groupIndex].suggestions.push(result.word);
  });

  return suggestions;
});

const fuzzySuggestionsInSearchWordClass = computed<string[]>(() => {
  const suggestions = fuzzySuggestions.value.find(
    (group) => group.wordClass === searchWordClass.value
  );

  if (suggestions) {
    return suggestions.suggestions;
  }

  return [];
});

const hasResultsInSearchWordClass = computed<boolean>(
  () => [...exactResults.value, ...fuzzySuggestionsInSearchWordClass.value].length > 0
);

const searchedWord = ref<string>(query.value);
const switchedFromWordClass = ref<WordClass | null>(null);

const onSearch = async () => {
  switchedFromWordClass.value = null;
  showWordExistsInOtherWordClassInfoBox.value = false;
  showPlaceholder.value = false;
  response.value = undefined;

  if (!query.value) {
    searchedWord.value = "";
    showPlaceholder.value = true;
    return;
  }

  searchedWord.value = query.value.trim();

  try {
    MatomoHelper.sendEvent(
      MatomoEventCategory.AbcWorkshop,
      MatomoEventAction.ClickInflectionTool,
      "Searched in inflection tool"
    );

    state.value = "loading";
    const apiResponse = await InflectionTrainerService.search(searchedWord.value);
    state.value = "success";

    response.value = apiResponse.data.data;

    syncQueryParam(searchedWord.value);

    // Only exact results can trigger jumping
    if (response.value.type !== "exact") {
      return;
    }

    if (response.value.results.length === 0) {
      return;
    }

    // Trigger jumping on no results in wordclass
    if (exactResults.value.length === 0) {
      const sortedResults = [...response.value.results].sort((a, b) => a.priority - b.priority);

      routeParameterSyncMethod.value = "replace";
      await flushVueQueue();

      switchedFromWordClass.value = searchWordClass.value;
      searchWordClass.value = sortedResults[0].wordClass;

      await flushVueQueue();
      routeParameterSyncMethod.value = "push";

      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcWorkshop,
        MatomoEventAction.ClickInflectionTool,
        "Automatically switched wordclass"
      );
    }
  } catch (error) {
    state.value = "error";
  }
};

const onFuzzySuggestionSearch = async (suggestion: string, wordClass: WordClass) => {
  syncQueryParam(suggestion);

  if (searchWordClass.value !== wordClass) {
    routeParameterSyncMethod.value = "replace";
    await flushVueQueue();

    searchWordClass.value = wordClass;
    switchedFromWordClass.value = null;

    await flushVueQueue();
    routeParameterSyncMethod.value = "push";
  }

  onSearch();
};

const onSearchBarReset = () => {
  onSearch();
};

onMounted(() => {
  if (query.value) {
    onSearch();
  }
});

// Make sure we search on back/forward buttons
useEventListener(window, "popstate", () => {
  if (route.query.query === undefined) {
    query.value = "";
    onSearch();
    return;
  }

  if (!isString(route.query.query)) {
    return;
  }

  if (route.query.query !== searchedWord.value) {
    syncQueryParam(route.query.query);
    onSearch();
  }
});

watch(fuzzySuggestionsInSearchWordClass, (suggestions) => {
  if (!response.value) {
    return;
  }

  if (response.value.type === "empty") {
    showPlaceholder.value = false;
  }

  if (response.value.type !== "fuzzy") {
    return;
  }

  showPlaceholder.value = suggestions.length > 0;
});

const showWordExistsInOtherWordClassInfoBox = ref<boolean>(false);

const alternativeWordClasses = computed<WordClass[]>(() => {
  if (!response.value || response.value.type === "fuzzy") {
    return [];
  }

  const resultsInOtherWordClass = response.value.results.filter(
    (result) => result.wordClass !== searchWordClass.value
  );

  return uniq(resultsInOtherWordClass.map((result) => result.wordClass));
});

watch(
  alternativeWordClasses,
  (newValue) => {
    showWordExistsInOtherWordClassInfoBox.value = Boolean(newValue);
  },
  { deep: true }
);

const onChangeWordClass = (
  source: "dropdown" | "info-box" | "fuzzy-suggestion",
  wordClass: WordClass
) => {
  searchWordClass.value = wordClass;
  switchedFromWordClass.value = null;

  MatomoHelper.sendEvent(
    MatomoEventCategory.AbcWorkshop,
    MatomoEventAction.ClickInflectionTool,
    `Switched wordclass using the ${source}`
  );
};

const isNounInflectionScheme = (
  inflectionScheme: DictsysGermanInflectionScheme
): inflectionScheme is Required<DictsysGermanNounInflectionScheme> => "noun" in inflectionScheme;

const isVerbInflectionScheme = (
  inflectionScheme: DictsysGermanInflectionScheme
): inflectionScheme is Required<DictsysGermanVerbInflectionScheme> => "verb" in inflectionScheme;

const isAdjectiveInflectionScheme = (
  inflectionScheme: DictsysGermanInflectionScheme
): inflectionScheme is Required<DictsysGermanAdjectiveInflectionScheme> =>
  "adjective" in inflectionScheme;

const trackInflectionSchemeTabClick = (name: string) => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.AbcWorkshop,
    MatomoEventAction.ClickInflectionTool,
    `Clicked "${name}" inflection scheme tab`
  );
};
</script>

<template>
  <div class="inflection-trainer mb-5">
    <div class="container p-0">
      <AbcHeading size="lg" tag="h1">{{ t("inflection-trainer.heading") }}</AbcHeading>
      <p class="inflection-trainer__description">{{ t("inflection-trainer.description") }}</p>

      <div class="inflection-trainer__container">
        <SearchBarBase
          v-model:query="query"
          class="inflection-trainer__search-bar"
          @search="onSearch()"
          @reset="onSearchBarReset()"
          @auto-focus-reset="query = ''"
        >
          <template #actions>
            <AbcDropdown
              :model-value="searchWordClass"
              :a11y-label="t('searchBar.dropdownLabel')"
              color="primary"
              class="inflection-trainer__search-dropdown"
              :options="dropdownWordClasses"
              :disabled="state === 'loading'"
              @update:model-value="onChangeWordClass('dropdown', $event as WordClass)"
            />
          </template>
        </SearchBarBase>

        <div class="inflection-trainer__info-box-container">
          <FuzzySuggestionsMessage
            v-if="fuzzySuggestions.length"
            :suggestions="fuzzySuggestions"
            :word-class="searchWordClass"
            @search="onFuzzySuggestionSearch($event.suggestion, $event.wordClass)"
            @change-word-class="onChangeWordClass('fuzzy-suggestion', $event)"
          />

          <WordClassSwitchedInfoBox
            v-if="switchedFromWordClass"
            :from="switchedFromWordClass"
            :to="searchWordClass"
            :word="searchedWord"
            @close="switchedFromWordClass = null"
          />

          <WordExistsInOtherWordClassInfoBox
            v-if="showWordExistsInOtherWordClassInfoBox && alternativeWordClasses.length === 1"
            :key="alternativeWordClasses[0]"
            :word="searchedWord"
            :word-class="alternativeWordClasses[0]"
            @close="showWordExistsInOtherWordClassInfoBox = false"
            @word-class-click="onChangeWordClass('info-box', $event)"
          />
          <WordExistsInOtherWordClassesInfoBox
            v-else-if="showWordExistsInOtherWordClassInfoBox && alternativeWordClasses.length > 1"
            :key="alternativeWordClasses.join('-')"
            :word="searchedWord"
            :word-classes="alternativeWordClasses"
            @close="showWordExistsInOtherWordClassInfoBox = false"
            @word-class-click="onChangeWordClass('info-box', $event)"
          />
        </div>

        <template v-if="showPlaceholder">
          <InflectionSchemeArticle
            :word-class="searchWordClass"
            :word-class-labels="wordClassLabels"
            class="mb-5"
          >
            <PlaceholderNounInflectionScheme v-if="searchWordClass === 'noun'" />
            <PlaceholderVerbInflectionScheme v-else-if="searchWordClass === 'verb'" />
            <PlaceholderAdjectiveInflectionScheme v-else-if="searchWordClass === 'adjective'" />
          </InflectionSchemeArticle>
        </template>
        <template v-else>
          <EmptyResultContainer v-if="state === 'success' && !hasResultsInSearchWordClass" />

          <div v-else-if="response?.type === 'exact'" class="inflection-trainer__container mb-5">
            <InflectionSchemeArticle
              v-for="(result, index) in exactResults"
              :key="index"
              :lemma="result.lemma"
              :word-class="result.wordClass"
              :word-class-labels="wordClassLabels"
              :gender="result.gender ? genderMap[result.gender] ?? null : null"
            >
              <NounInflectionSchemeModal
                v-if="searchWordClass === 'noun' && isNounInflectionScheme(result.inflectionScheme)"
                :inflection-scheme="result.inflectionScheme.noun"
                @show-tab="trackInflectionSchemeTabClick($event)"
              >
                <template #inflection-cell="{ inflections }">
                  <InflectionSchemeCellDiff :diff-word="result.lemma" :inflections="inflections" />
                </template>
              </NounInflectionSchemeModal>

              <VerbInflectionSchemeModal
                v-else-if="
                  searchWordClass === 'verb' && isVerbInflectionScheme(result.inflectionScheme)
                "
                :inflection-scheme="result.inflectionScheme.verb"
                @show-tab="trackInflectionSchemeTabClick($event)"
              >
                <template #inflection-cell="{ inflections }">
                  <!-- Trying to diff a verb is hopeless, because it can change so much, especially loose-conjugated words. -->
                  <InflectionSchemeCellHighlight :inflections="inflections" />
                </template>
              </VerbInflectionSchemeModal>

              <AdjectiveInflectionSchemeModal
                v-else-if="
                  searchWordClass === 'adjective' &&
                  isAdjectiveInflectionScheme(result.inflectionScheme)
                "
                :inflection-scheme="result.inflectionScheme.adjective"
                hide-comparisons
                @show-tab="trackInflectionSchemeTabClick($event)"
              >
                <template #inflection-cell="{ inflections }">
                  <InflectionSchemeCellDiff :diff-word="result.lemma" :inflections="inflections" />
                </template>
              </AdjectiveInflectionSchemeModal>
            </InflectionSchemeArticle>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.inflection-trainer {
  &__container {
    display: flex;
    flex-direction: column;
    gap: 19px;
  }

  &__description {
    margin-top: 8px;
    margin-bottom: 36px;
    font-size: 1rem;
    line-height: 1.5rem;
  }

  &__search-dropdown {
    min-width: 175px;
    max-height: 47px;
    margin-top: 9.6px;
    font-size: 0.81rem;

    #media-breakpoint-up(lg, {
      margin-top: 0;
      margin-left: 10px;
    });
  }

  &__fuzzy {
    color: rgb(@theme-colors[@primary][base]);
  }

  &__info-box-container {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
}
</style>
